"use client";

import { Globe, Puzzle, Terminal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ITLSStandard() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const plugins = [{
    name: "strategy-audit",
    version: "v1.8.2",
    verified: true
  }, {
    name: "project-sync",
    version: "v2.4.0",
    verified: false
  }, {
    name: "resource-map",
    version: "v0.9.7",
    verified: false
  }, {
    name: "risk-analysis",
    version: "v1.2.1",
    verified: false
  }];
  const providers = ["Consulting", "Management", "Strategy", "Operations", "Analytics", "Support"];
  const cards = [{
    title: "Strategic Clarity",
    icon: Terminal,
    body: "A singular framework for complex business challenges. No fragmented processes, just unified execution.",
    type: "code" as const
  }, {
    title: "Tailored Solutions",
    icon: Puzzle,
    body: "Extend our core expertise with bespoke modules. Integrate your specific goals into our proven methodology.",
    type: "plugins" as const
  }, {
    title: "Industry Reach",
    icon: Globe,
    body: "Comprehensive coverage across diverse sectors — from emerging startups to established enterprises.",
    type: "providers" as const
  }];
  return <section data-section-id="11208" id="the-itls-standard" className="dark bg-background text-foreground border-t border-border py-24 md:py-32 font-default">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
          <div className="lg:col-span-7">
            <h2 className="font-default font-medium leading-[1.04] tracking-[-0.03em]" style={{
            fontSize: "clamp(2rem, 3.4vw, 3rem)"
          }}>
              Precision Insight.{" "}
              <em className="italic font-normal text-muted-foreground">
                Tailored Excellence.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-muted-foreground leading-[1.55]" style={{
            fontSize: "clamp(1.05rem, 1.15vw, 1.18rem)"
          }}>
              {"ITLS delivers professional consulting through a transparent, innovative interface — ensuring your business objectives are met with absolute clarity."}
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, i) => {
          const Icon = card.icon;
          return <Card key={card.title} data-index={i} className="group relative bg-card border border-border rounded-lg p-6 gap-0 py-0 overflow-hidden transition-colors duration-200">
                <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-primary scale-x-0 origin-left transition-transform duration-200 ease-out group-hover:scale-x-100" />
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-default font-medium text-[1.15rem] md:text-[1.25rem] tracking-[-0.015em] leading-[1.2] text-foreground">
                    {card.title}
                  </h3>
                  <Icon className="size-4 text-muted-foreground shrink-0 mt-1" strokeWidth={1.5} />
                </div>
                <p className="mt-3 text-[0.95rem] leading-[1.6] text-muted-foreground max-w-[30ch]">
                  {card.body}
                </p>

                <div className="mt-6 h-px w-full bg-border" />

                <div className="mt-6">
                  {card.type === "code" && <pre className="font-mono text-[0.8rem] leading-[1.6] text-foreground overflow-x-auto">
                      <code>
                        <span className="text-muted-foreground">{"// itls.config.ts"}</span>
                        {"\n"}
                        <span className="text-muted-foreground">service</span>
                        <span>{": "}</span>
                        <span className="text-primary">{'"consulting"'}</span>
                        {"\n"}
                        <span className="text-muted-foreground">focus</span>
                        <span>{":   "}</span>
                        <span className="text-primary">{'"growth"'}</span>
                        {"\n"}
                        <span className="text-muted-foreground">phase</span>
                        <span>{":    "}</span>
                        <span className="text-primary">{'"execution"'}</span>
                        {"\n"}
                        <span className="text-muted-foreground">impact</span>
                        <span>{": "}</span>
                        <span className="text-foreground">100%</span>
                        {"\n"}
                        <span className="text-muted-foreground">verified</span>
                        <span>{":   "}</span>
                        <span className="text-foreground">true</span>
                      </code>
                    </pre>}

                  {card.type === "plugins" && <ul className="flex flex-col">
                      {plugins.map((p, j) => <li data-index={j} key={p.name} className={`flex items-center justify-between gap-3 py-2.5 ${j !== plugins.length - 1 ? "border-b border-border" : ""}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[0.85rem] text-foreground truncate">
                              {p.name}
                            </span>
                            {p.verified && <Badge className="bg-primary/10 text-primary border border-primary/30 rounded-sm px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-[0.08em]" variant="outline">
                                Verified
                              </Badge>}
                          </div>
                          <span className="font-mono text-[0.8rem] text-muted-foreground shrink-0 tabular-nums">
                            {p.version}
                          </span>
                        </li>)}
                    </ul>}

                  {card.type === "providers" && <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                      {providers.map((name, j) => <span data-index={j} key={name} className="font-mono text-[0.85rem] text-muted-foreground opacity-60 hover:opacity-100 hover:text-foreground transition-all duration-150 cursor-default truncate">
                          {name}
                        </span>)}
                    </div>}
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}